import { Inject, Injectable } from '@angular/core';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';

/**
 * Resolves whether cost assumptions are eligible to be shown or edited
 * based on the current filter context.
 *
 * Eligibility rules:
 * - Division is NOT Distributor
 * - Exactly ONE region is selected (global TVD is not supported)
 *
 * Callers may compose additional context-specific conditions on top
 * (e.g. isDivisionFeatureEnabled() in the landing page, isCustomMetric
 * in the drilldown page).
 */
@Injectable({ providedIn: 'root' })
export class CostAssumptionsEligibilityResolver {

  /**
   * LA and IMEA only support cost assumptions for the Institutional division —
   * QSR and FRS must remain hidden in those regions.
   */
  private readonly LA_IMEA_SUPPORTED_DIVISIONS = new Set<string>(['Institutional']);
  // Regions where Cost Assumptions are supported only for the Institutional (INST) division. QSR and FRS divisions are not supported in these regions.
  private readonly REGIONS_RESTRICTED = new Set<string>(['LA', 'IMEA']);

  constructor(
    private _filtersService: FiltersService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags
  ) {}

  /**
   * Resolve eligibility using the current filters from FiltersService.
   */
  resolve(): boolean {
    const filters = this._filtersService.selectedFilters?.getValue();
    return this.resolveFromFilters(filters);
  }

  /**
   * Resolve eligibility using explicit filters.
   * Useful for testing or flows where filters are passed directly.
   */
  // TODO: Consider deprecating this method or making it private in favor of the parameterless resolve().
  resolveFromFilters(filters: ContractModel | undefined): boolean {
    if (!filters) return false;

    const isDistributor = filters.division === 'Distributor';
    const isSingleRegion = filters.regions?.length === 1;

    if (isDistributor || !isSingleRegion) return false;

    return this.isDivisionFeatureEnabled(filters.division, filters.regions?.[0] ?? '');
  }

  /**
   * Determines whether the current division is eligible for cost assumptions in the given region.
   * - LA/IMEA: only the Institutional division is eligible (QSR and FRS are always hidden there,
   *   regardless of the QSR feature flag).
   * - NA/EU/AP: QSR eligibility depends on the enableCostAssumptionsQSR feature flag; all other
   *   divisions are eligible by default.
   */
  private isDivisionFeatureEnabled(division: string | undefined, region: string): boolean {
    if (this.REGIONS_RESTRICTED.has(region)) {
      return this.LA_IMEA_SUPPORTED_DIVISIONS.has(division ?? '');
    }

    if (division === 'QSR')
      return this._featureFlags.enableCostAssumptionsQSR;

    return true;
  }
}
